import * as React from 'react';
import styled, {
  DefaultTheme,
  ThemeProvider as StyledComponentsThemeProvider,
} from 'styled-components';
import {
  buildInternalThemeVariablesCss,
  // This file is the one exception where we do want to import it.
  _dontImportMeInternalTheme as internalTheme,
} from './internal-theme';
import {
  EMobilityToolsFeatureAppTheme,
  NaturalLanguageFeatureAppTheme,
  TariffsFeatureAppTheme,
} from './types';

export type EMobilityToolsFeatureAppThemeProviderProps<T> =
  React.PropsWithChildren<{
    readonly theme: T;
  }>;

// Note: we have to merge the parent theme into our theme, so that if a brand
// passes in a styled component, it can make use of the brand's own
// styled-component theme.
function mergeThemes<T>(
  brandInternalTheme: Record<string, unknown>,
  brandWhiteLabelTheme: T
): DefaultTheme {
  return {
    ...brandInternalTheme,
    brand: brandWhiteLabelTheme,
    internal: internalTheme,
  };
}

// We should not use our theme in here yet!
const ThemeVariablesWrapper = styled.div`
  display: contents;
  ${buildInternalThemeVariablesCss()}
`;

/**
 *  ThemeProvider component
 *  Provides a theme object to all it's child components via `styled-components`' ThemeProvider
 */
export function EMobilityToolsFeatureAppThemeProvider<T>(
  props: EMobilityToolsFeatureAppThemeProviderProps<T>
): JSX.Element | null {
  const {children, theme} = props;

  const validChildren: React.ReactElement[] = React.Children.toArray(
    children
  ).filter(React.isValidElement);

  if (validChildren.length === 0) {
    return null;
  }

  return (
    <StyledComponentsThemeProvider
      theme={(currentTheme) =>
        // The types aren't correct here, the "currentTheme" is going to be the brand internal one, not ours
        mergeThemes<T>(
          currentTheme as unknown as Record<string, unknown>,
          theme
        )
      }
    >
      <ThemeVariablesWrapper>{validChildren}</ThemeVariablesWrapper>
    </StyledComponentsThemeProvider>
  );
}

export function WhiteLabelThemeProvider(
  props: EMobilityToolsFeatureAppThemeProviderProps<EMobilityToolsFeatureAppTheme>
): JSX.Element | null {
  return <EMobilityToolsFeatureAppThemeProvider {...props} />;
}

export function TariffsThemeProvider(
  props: EMobilityToolsFeatureAppThemeProviderProps<TariffsFeatureAppTheme>
): JSX.Element | null {
  return <EMobilityToolsFeatureAppThemeProvider {...props} />;
}

export function NaturalLanguageThemeProvider(
  props: EMobilityToolsFeatureAppThemeProviderProps<NaturalLanguageFeatureAppTheme>
): JSX.Element | null {
  return <EMobilityToolsFeatureAppThemeProvider {...props} />;
}
